import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileTerapiOpen, setMobileTerapiOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const menuItems = [
    { label: 'Hjem', href: '/' },
    { 
      label: 'Terapi', 
      isDead: true,
      subItems: [
        { label: 'Samtaleterapi', href: '/terapi/samtaleterapi' },
        { label: 'Online terapi', href: '/terapi/online-terapi' },
        { label: 'Walk and talk', href: '/terapi/walk-and-talk' },
        { label: 'Tegne- / kunstterapi', href: '/terapi/tegne-kunstterapi' }
      ]
    },
    { label: 'Om', href: '/om' },
    { label: 'CV', href: '/cv' },
    { label: 'Klinikken', href: '/klinikken' },
    { label: 'Priser', href: '/priser' },
    { label: 'Udtalelser', href: '/udtalelser' },
    { label: 'Info', href: '/velkomstbrev' },
    { label: 'Kontakt', href: '/kontakt' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Open mobile terapi dropdown if user is on a therapy page
  useEffect(() => {
    if (location.pathname.startsWith('/terapi')) {
      setMobileTerapiOpen(true);
    }
  }, [location.pathname]);

  const isActive = (item) => {
    if (item.isDead) {
      return item.subItems?.some(sub => location.pathname === sub.href);
    }
    if (item.href === '/') return location.pathname === '/';
    return location.pathname.startsWith(item.href);
  };

  return (
    <header 
      className={`sticky top-0 z-50 bg-white transition-all duration-300 ${
        scrolled 
          ? 'shadow-md border-b border-[#E8E2D5]' 
          : 'border-b border-[#EDE6D8]'
      }`}
    >
      {/* Top Banner / Micro Bar */}
      <div className="bg-[#5f7365] text-[#E8ECE9] text-xs sm:text-sm py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center flex-wrap gap-2">
          <div className="flex items-center gap-4 text-white">
            <span className="inline-flex items-center gap-1.5">
              <i className="fa-regular fa-clock text-white" aria-hidden="true" />
              Telefontid: 12.45 - 13.00 hverdage (undtagen onsdage)
            </span>
          </div>
          <div className="flex items-center gap-4 ml-auto">
            <a 
              href="tel:29921879" 
              className="inline-flex items-center gap-1.5 font-medium text-white hover:text-[#C5DCD0] transition-colors whitespace-nowrap"
            >
              <i className="fa-regular fa-phone text-white" aria-hidden="true" />
              <span className="whitespace-nowrap">29 92 18 79</span>
            </a>
            <a 
              href="mailto:kontakt@dinprivatpsykolog.dk" 
              className="hidden sm:inline-flex items-center gap-1.5 text-white hover:text-[#C5DCD0] transition-colors whitespace-nowrap"
            >
              <i className="fa-regular fa-envelope text-white" aria-hidden="true" />
              <span className="whitespace-nowrap">kontakt@dinprivatpsykolog.dk</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Header Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        {/* Brand Logo / Name */}
        <Link 
          to="/" 
          className="group flex items-center gap-3 sm:gap-4 focus:outline-none focus:ring-2 focus:ring-[#5f7365] rounded-sm p-1 transition-opacity hover:opacity-95"
          aria-label="Marie Aagaard Larsen - Gå til forsiden"
        >
          {/* MÅL Monogram & Marie Aagaard Larsen lockup */}
          <div className="flex flex-col items-center shrink-0">
            <img
              src="https://assets.auxodev.dk/assets/9bc67d49-c1ad-4360-8231-e9811640bce9/logo-marie.png"
              alt="Marie Aagaard Larsen"
              className="h-10 sm:h-12 w-auto object-contain group-hover:scale-105 transition-transform duration-200"
            />
          </div>
        </Link>

        {/* Desktop Navigation (VISIBLE AT 1200px AND ABOVE: min-[1200px]:flex) */}
        <nav className="hidden min-[1200px]:flex items-center space-x-1 lg:space-x-2">
          {menuItems.map((item) => {
            const active = isActive(item);
            return (
              <div key={item.label} className="relative group">
                {item.isDead ? (
                  <button
                    type="button"
                    className={`px-3 py-2 text-sm font-medium transition-colors inline-flex items-center gap-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#5f7365] ${
                      active
                        ? 'text-[#5f7365] font-bold'
                        : 'text-[#5f7365] hover:text-[#4e6053]'
                    }`}
                  >
                    {item.label}
                    {item.subItems && (
                      <i className="fa-regular fa-chevron-down text-[10px] opacity-70 group-hover:rotate-180 transition-transform duration-200" aria-hidden="true" />
                    )}
                  </button>
                ) : (
                  <Link
                    to={item.href}
                    className={`px-3 py-2 text-sm font-medium transition-colors inline-flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-[#5f7365] ${
                      active
                        ? 'text-[#5f7365] font-bold'
                        : 'text-[#5f7365] hover:text-[#4e6053]'
                    }`}
                    aria-current={active ? 'page' : undefined}
                  >
                    {item.label}
                    {item.subItems && (
                      <i className="fa-regular fa-chevron-down text-[10px] opacity-70 group-hover:rotate-180 transition-transform duration-200" aria-hidden="true" />
                    )}
                  </Link>
                )}

                {/* Submenu dropdown if applicable */}
                {item.subItems && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-none shadow-lg border border-[#EDE6D8] py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 transform origin-top-left group-hover:translate-y-0 translate-y-1">
                    {item.subItems.map((sub) => {
                      const isSubActive = location.pathname === sub.href;
                      return (
                        <Link
                          key={sub.label}
                          to={sub.href}
                          className={`block px-4 py-2.5 text-sm transition-colors ${
                            isSubActive
                              ? 'text-[#5f7365] font-bold'
                              : 'text-[#5f7365] hover:text-[#4e6053]'
                          }`}
                        >
                          {sub.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          <Link
            to="/kontakt"
            className="ml-3 inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-[#5f7365] hover:bg-[#4e6053] rounded-none transition-all duration-200 hover:-translate-y-1 shadow-xs whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5f7365]"
          >
            <i className="fa-regular fa-calendar-check mr-2" aria-hidden="true" />
            Bestil tid
          </Link>
        </nav>

        {/* Mobile / Tablet Burger Button (VISIBLE BELOW 1200px: max-[1199px]:flex min-[1200px]:hidden) */}
        <div className="flex items-center min-[1200px]:hidden gap-2">
          <a
            href="tel:29921879"
            className="p-2 text-[#5f7365] hover:text-[#4e6053] text-lg focus:outline-none transition-colors"
            title="Ring til Marie: 29 92 18 79"
            aria-label="Ring til 29 92 18 79"
          >
            <i className="fa-regular fa-phone" aria-hidden="true" />
          </a>
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-[#5f7365] hover:text-[#4e6053] focus:outline-none outline-none border-none transition-colors cursor-pointer bg-transparent active:bg-transparent"
            aria-expanded={mobileMenuOpen}
            aria-label="Åbn Hovedmenu"
          >
            <i className={`fa-regular ${mobileMenuOpen ? 'fa-xmark text-xl' : 'fa-bars text-xl'}`} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Mobile Drawer Menu (Under 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-white border-b border-[#EDE6D8] px-4 pt-2 pb-6 space-y-2 shadow-xl animate-in slide-in-from-top-2 duration-200">
          {menuItems.map((item) => {
            const active = isActive(item);
            return (
              <div key={item.label} className="space-y-1">
                {item.isDead ? (
                  <button
                    type="button"
                    onClick={() => setMobileTerapiOpen(!mobileTerapiOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 text-base font-medium text-[#5f7365] hover:text-[#4e6053] transition-colors text-left cursor-pointer"
                  >
                    <span className={active ? 'text-[#5f7365] font-bold' : ''}>{item.label}</span>
                    <i 
                    className={`fa-regular fa-chevron-down text-xs transition-transform duration-200 ${
                      mobileTerapiOpen ? 'rotate-180' : ''
                    }`} 
                    aria-hidden="true" 
                  />
                  </button>
                ) : (
                  <Link
                    to={item.href}
                    className={`block px-4 py-3 text-base font-medium transition-colors ${
                      active
                        ? 'text-[#5f7365] font-bold'
                        : 'text-[#5f7365] hover:text-[#4e6053]'
                    }`}
                    aria-current={active ? 'page' : undefined}
                  >
                    {item.label}
                  </Link>
                )}
                {item.subItems && mobileTerapiOpen && (
                  <div className="pl-6 space-y-1 my-1 border-l-2 border-[#E8ECE9] ml-4">
                    {item.subItems.map((sub) => {
                      const isSubActive = location.pathname === sub.href;
                      return (
                        <Link
                          key={sub.label}
                          to={sub.href}
                          className={`block px-3 py-2 text-sm transition-colors ${
                            isSubActive
                              ? 'text-[#5f7365] font-bold'
                              : 'text-[#465A4F] hover:text-[#5f7365]'
                          }`}
                        >
                          {sub.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          <div className="pt-4 border-t border-[#EDE6D8] space-y-3">
            <div className="bg-[#EFEAE1] p-3.5 rounded-lg space-y-2 text-sm text-[#334238]">
              <p className="font-semibold text-[#5f7365] flex items-center gap-2">
                <i className="fa-regular fa-clock text-[#5f7365]" />
                Telefontid
              </p>
              <p className="text-xs">12.45 - 13.00 hverdage (undtagen onsdage)</p>
              <a 
                href="tel:29921879" 
                className="inline-flex items-center gap-2 text-[#5f7365] font-bold hover:underline whitespace-nowrap pt-1"
              >
                <i className="fa-regular fa-phone" />
                <span className="whitespace-nowrap">29 92 18 79</span>
              </a>
            </div>

            <Link
              to="/kontakt"
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-[#5f7365] text-white font-semibold rounded-none hover:bg-[#4e6053] transition-all duration-200 hover:-translate-y-1"
            >
              <i className="fa-regular fa-envelope" aria-hidden="true" />
              Kontakt / Bestil tid
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
